import { FC, Fragment, useState } from 'react'
import { PrismicRichText } from '@prismicio/react'
import { ImageField } from '@prismicio/client'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import tw from 'twin.macro'
import { Body, Grid, SmallBody, SmallHeader, SubHeader } from '../UI'
import { RichTextFields } from '../../types/prismic'
import { Disclosure } from '@headlessui/react'
import {
  richTextComponents,
  richTextStrongComponent,
} from '../../utils/richTextComponents'

type Props = {
  header: string
  subhead: RichTextFields
  existing_solutions: Array<{
    solution_header: string
    solution_copy: RichTextFields
    solution_citation: string
    solution_image: ImageField
  }>
}

const Solutions: FC<Props> = ({ header, subhead, existing_solutions }) => {
  const [activeItem, setActiveItem] = useState(0)
  return (
    <section tw="bg-gb-cream pt-[57px] pb-[88px] lg:(pt-[64px] pb-[107px]) text-gb-blue font-domaine">
      <Grid>
        {header && (
          <div tw="col-span-full lg:(col-span-5)">
            <h2 css={[Body, tw`mb-[15px] lg:mb-[25px]`]}>{header}</h2>
          </div>
        )}
        <div tw="col-span-full mb-8 lg:(col-start-1 col-span-full mb-5 pr-6)">
          <PrismicRichText
            field={subhead.richText}
            components={richTextComponents}
          />
        </div>
      </Grid>
      {/* Image and Content Disclosures - Mobile */}
      <Grid tw="lg:hidden">
        <div tw="col-span-full">
          {existing_solutions &&
            existing_solutions.map((el, index) => (
              <div tw="mb-5" key={`mobile-existing-solutions-${index}`}>
                <Disclosure>
                  {({ open }) => (
                    <Fragment>
                      <Disclosure.Button
                        css={[
                          tw`w-full text-left py-[24px] px-[20px] border border-gb-blue`,
                          open && tw`text-white border-0`,
                          open && index % 3 === 0 && tw`bg-gb-magenta`,
                          open && index % 3 === 1 && tw`bg-gb-red`,
                          open && index % 3 === 2 && tw`bg-gb-blue`,
                        ]}
                      >
                        <h4 css={[SubHeader]}>{el.solution_header}</h4>
                      </Disclosure.Button>
                      <Disclosure.Panel
                        css={[
                          tw`text-white`,
                          index % 3 === 0 && tw`bg-gb-magenta`,
                          index % 3 === 1 && tw`bg-gb-red`,
                          index % 3 === 2 && tw`bg-gb-blue`,
                        ]}
                      >
                        <GatsbyImage
                          image={getImage(el.solution_image)}
                          css={[tw`w-full`]}
                          alt=""
                        />
                        <div tw="px-5 py-6">
                          <p>
                            <PrismicRichText
                              field={el.solution_copy.richText}
                              components={richTextStrongComponent}
                            />
                          </p>
                          {el.solution_citation && (
                            <div css={[SmallBody, tw`col-span-full mt-4`]}>
                              {el.solution_citation}
                            </div>
                          )}
                        </div>
                      </Disclosure.Panel>
                    </Fragment>
                  )}
                </Disclosure>
              </div>
            ))}
        </div>
      </Grid>

      {/* Image and Content - Desktop */}
      <Grid tw="hidden lg:grid">
        <div tw="col-span-full lg:col-span-5">
          <ul css={[SmallHeader]}>
            {existing_solutions &&
              existing_solutions.map((el, index) => (
                <li
                  key={`desktop-existing-solution-${index}`}
                  css={[
                    tw`lg:(py-[25px] px-[21px] border border-gb-blue text-gb-blue mb-[15px] cursor-pointer)`,
                    index === activeItem &&
                      index % 3 === 0 &&
                      tw`lg:(border-gb-magenta bg-gb-magenta text-white)`,
                    index === activeItem &&
                      index % 3 === 1 &&
                      tw`lg:(border-gb-red bg-gb-red text-white)`,
                    index === activeItem &&
                      index % 3 === 2 &&
                      tw`lg:(border-gb-blue bg-gb-blue text-white)`,
                  ]}
                  onClick={() => {
                    setActiveItem(index)
                  }}
                  aria-hidden="true"
                >
                  {el.solution_header}
                </li>
              ))}
          </ul>
        </div>
        <div tw="relative col-start-7 col-span-6">
          {existing_solutions &&
            existing_solutions.map((el, index) => (
              <GatsbyImage
                key={`desktop-existing-solution-image-${index}`}
                image={getImage(el.solution_image)}
                css={[
                  tw`hidden absolute top-0 right-0 w-5/6 h-[364px] object-cover`,
                  activeItem === index && tw`block`,
                ]}
                alt=""
              />
            ))}
          {/* Copy */}
          <div
            css={[
              tw`text-white w-4/6 px-[25px] py-[30px] absolute bottom-0 left-0`,
              activeItem % 3 === 0 && tw`lg:(border-gb-magenta bg-gb-magenta)`,
              activeItem % 3 === 1 && tw`lg:(border-gb-red bg-gb-red)`,
              activeItem % 3 === 2 && tw`lg:(border-gb-blue bg-gb-blue)`,
            ]}
          >
            {existing_solutions &&
              existing_solutions.map((el, index) => (
                <Fragment>
                  <p
                    key={`desktop-existing-solution-copy-${index}`}
                    css={[
                      tw`hidden`,
                      activeItem === index && tw`block line-height[1.3]`,
                    ]}
                  >
                    <PrismicRichText
                      field={el.solution_copy.richText}
                      components={richTextStrongComponent}
                    />
                  </p>
                  {el.solution_citation && (
                    <div
                      css={[
                        SmallBody,
                        tw`hidden`,
                        activeItem === index && tw`block mt-4`,
                      ]}
                    >
                      {el.solution_citation}
                    </div>
                  )}
                </Fragment>
              ))}
          </div>
        </div>
      </Grid>
    </section>
  )
}

export default Solutions
